import { useSmartMutation, useSmartQuery } from "@shared/api/queries/hooks";
import { queryClient } from "@shared/api/queries/queryClient";
import { QueryKeyFactory } from "@shared/api/queries/queryKeyFactory";
import { toast } from "@shared/hooks/use-toast";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { useNavigate } from "react-router-dom";
import { failuresApi } from "./api";

export const useAddFailures = () => {
  const remoteUtilityId = useRemoteUtilityId();
  const navigate = useNavigate();
  return useSmartMutation((payload: any) => failuresApi.addFailures(payload), {
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Failure report submitted successfully.",
      });
      localStorage.removeItem("failureReportDraft");
      navigate("/ax/field-alerts/failure/");
      queryClient.refetchQueries({
        queryKey: QueryKeyFactory.module.ax.failures
          .failuresList({
            remote_utility_id: remoteUtilityId,
          })
          .slice(0, 2),
      });
      queryClient.refetchQueries({
        queryKey: QueryKeyFactory.module.ax.failures
          .failuresDashboard({
            remote_utility_id: remoteUtilityId,
          })
          .slice(0, 2),
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });
};

export const useFailuresDashboard = (params: { remote_utility_id: any }) => {
  return useSmartQuery(
    QueryKeyFactory.module.ax.failures.failuresDashboard(params),
    () => failuresApi.getFailuresDashboard(params)
  );
};

export const useAssetByMap = (params: {
  remote_utility_id: string;
  longitude: any;
  latitude: any;
}) => {
  return useSmartQuery(
    QueryKeyFactory.module.ax.failures.assetByMap(params),
    () => failuresApi.getAssetByMap(params)
  );
};
export const useObservedSymptom = () => {
  return useSmartQuery(
    QueryKeyFactory.module.ax.failures.observedSymptom(),
    () => failuresApi.getObservedSymptom()
  );
};
export const useFailureStatus = () => {
  return useSmartQuery(QueryKeyFactory.module.ax.failures.failureStatus(), () =>
    failuresApi.getFailureStatus()
  );
};
export const useOperationStatus = () => {
  return useSmartQuery(QueryKeyFactory.module.ax.failures.failureStatus(), () =>
    failuresApi.getOperationStatus()
  );
};
export const useFailuresList = (params: {
  remote_utility_id: any;
  search?: string;
}) => {
  return useSmartQuery(
    QueryKeyFactory.module.ax.failures.failuresList(params),
    () => failuresApi.getFailuresList(params)
  );
};

export const useSOPSelection = (params: {
  remote_utility_id: string;
  reading_source: string;
  association_type: string;
  operation_type: any;
}) => {
  return useSmartQuery(
    QueryKeyFactory.module.ax.failures.sopSelection(params),
    () => failuresApi.getSOPSelection(params),
    {
      enabled: Boolean(params?.operation_type),
    }
  );
};

export const useAssetsList = (params: {
  remote_utility_id: any;
  page: any;
  limit: any;
}) => {
  return useSmartQuery(
    QueryKeyFactory.module.ax.failures.assetList(params),
    () => failuresApi.getAssetList(params)
  );
};
export const useFailuresDetails = (params: {
  remote_utility_id: any;
  id: any;
}) => {
  return useSmartQuery(
    QueryKeyFactory.module.ax.failures.failuresDetails(params),
    () => failuresApi.getFailuresDetails(params),
    { enabled: !!params.id }
  );
};

export const useIgnoreFailure = () => {
  const remoteUtilityId = useRemoteUtilityId();
  return useSmartMutation(({ id, payload }: { id: any; payload: any }) =>
    failuresApi.ignoreFailures(id, payload)
  );
};

export const useUpdateFailure = () => {
  const remoteUtilityId = useRemoteUtilityId();
  const navigate = useNavigate();
  return useSmartMutation(
    ({ id, payload }: { id: any; payload: any }) =>
      failuresApi.updateFailures(id, payload),
    {
      onSuccess: () => {
        toast({
          title: "Success",
          description: "Failure report updated successfully.",
        });
        localStorage.removeItem("failureReportDraft");
        navigate("/ax/field-alerts/failure/");
        queryClient.refetchQueries({
          queryKey: QueryKeyFactory.module.ax.failures
            .failuresList({
              remote_utility_id: remoteUtilityId,
            })
            .slice(0, 2),
        });
        queryClient.refetchQueries({
          queryKey: QueryKeyFactory.module.ax.failures
            .failuresDashboard({
              remote_utility_id: remoteUtilityId,
            })
            .slice(0, 2),
        });
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description: error.message,
          variant: "destructive",
        });
      },
    }
  );
};

export const useFailureMode = (params: {
  remote_utility_id: string;
  asset_type_characteristics: any;
  config_level: any;
  code_list: any;
}) => {
  return useSmartQuery(
    QueryKeyFactory.module.ax.failures.failureModes(params),
    () => failuresApi.getFailureModes(params)
  );
};

export const useAddServiceOrder = (params: {
  failureId?: string;
  incidentId?: string;
}) => {
  const remoteUtilityId = useRemoteUtilityId();
  return useSmartMutation(
    (payload: any) => failuresApi.addServiceOrder(payload),
    {
      onSuccess: () => {
        if (params.failureId) {
          queryClient.refetchQueries({
            queryKey: QueryKeyFactory.module.ax.failures
              .failuresDetails({
                remote_utility_id: remoteUtilityId,
                id: params.failureId,
              })
              .slice(0, 2),
          });
          queryClient.refetchQueries({
            queryKey: QueryKeyFactory.module.ax.failures
              .failuresList({
                remote_utility_id: remoteUtilityId,
              })
              .slice(0, 2),
          });
        } else {
          const key = QueryKeyFactory.module.ax.incidents
            .incidentsList()
            .slice(0, 4);

          const incidentDetail = QueryKeyFactory.module.ax.incidents
            .incidentDetail(params.incidentId)
            .slice(0, 4);

          queryClient.refetchQueries({
            queryKey: incidentDetail,
          });

          queryClient.refetchQueries({
            queryKey: key,
            exact: false,
          });
        }
      },
    }
  );
};
